/**
 * Plausibility checks for the editable consensus inputs.
 *
 * These are WARNINGS, never blocking. Bounds are deliberately wide: they are
 * the historical range of each input in the platform's weekly panel
 * (2010–2026, `Weekly data.xlsx`) plus a margin, rounded. They flag typos
 * (a missing decimal point, a sign error), not unusual-but-possible scenarios.
 * The rules are documented here so a reviewer can change them deliberately.
 */

export const INPUT_RULES = {
  "Real GDP growth (% yoy)": { min: -15, max: 15, unit: "%" },
  "Inflation (% yoy - avg)": { min: -5, max: 100, unit: "%" },
  "Central Bank rate (% eop)": { min: -1, max: 100, unit: "%" },
  "10-year interest rate (% eop)": { min: -1, max: 60, unit: "%" },
  "USA GDP growth (% yoy)": { min: -10, max: 10, unit: "%" },
  "China GDP growth (% yoy)": { min: -5, max: 15, unit: "%" },
  "USA Inflation (% yoy - avg)": { min: -3, max: 20, unit: "%" },
  "USA Central Bank rate (% eop)": { min: 0, max: 20, unit: "%" },
  "USA 10-Year rate (% eop)": { min: 0, max: 15, unit: "%" },
  "USA - S&P500": { min: 1000, max: 20000, unit: "index pts" },
  "USA - VIX": { min: 8, max: 90, unit: "index pts" },
  EURUSD: { min: 0.6, max: 2, unit: "" },
  GBPUSD: { min: 0.8, max: 2.5, unit: "" },
  USDJPY: { min: 60, max: 250, unit: "" },
  "Oil Brent $/BBL": { min: 10, max: 250, unit: "$/BBL" },
  "Natural Gas $/MMBTU": { min: 0.5, max: 30, unit: "$/MMBTU" },
  "Aluminum $/MT": { min: 800, max: 8000, unit: "$/MT" },
  // The platform's copper series is quoted in $/lb-scale units (~6.5) even
  // though the row label says $/MT; the bound follows the data, not the label.
  "Copper $/MT": { min: 1, max: 20000, unit: "$/MT" },
  "Corn $/BU": { min: 150, max: 1500, unit: "$/BU" },
  "Soybeans $/BU": { min: 400, max: 3000, unit: "$/BU" },
};

/**
 * Validate one cell. Returns `null` when fine, otherwise
 * `{ level: "warning", code, message }`.
 */
export function validateCell(rowName, value) {
  if (value === null || value === undefined || value === "") {
    return { level: "warning", code: "empty", message: "Empty cell: the backend will treat it as missing." };
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return { level: "warning", code: "nan", message: `"${value}" is not a number.` };
  }
  const rule = INPUT_RULES[rowName];
  if (!rule) return null;
  if (n < rule.min || n > rule.max) {
    return {
      level: "warning",
      code: "range",
      message: `Outside the documented plausibility range ${rule.min} to ${rule.max}${rule.unit ? " " + rule.unit : ""}. Check the decimal point or sign.`,
    };
  }
  return null;
}

/** Validate all rows. Returns `[{rowIndex, colIndex, name, ...issue}]`. */
export function validateRows(rows) {
  const out = [];
  (rows || []).forEach((row, i) => {
    if (!row || row.header) return;
    row.data.forEach((v, j) => {
      const issue = validateCell(row.name, v);
      if (issue) out.push({ rowIndex: i, colIndex: j, name: row.name, ...issue });
    });
  });
  return out;
}
